import { Component, EventEmitter, computed, input, Output, signal } from '@angular/core';
import { updatedAtSignal } from '../analysis-data';
import { v3ComponentContext } from '../services/v3-component-context';
import { analystsAnalysisData } from '../services/analysts-analysis-data';
import { V3AnalystsDesktopContent } from '../v3-analysts-desktop-content/v3-analysts-desktop-content';
import { V3AnalystsStatsRow } from '../v3-analysts-stats-row/v3-analysts-stats-row';
import { V3OverviewCardHeader } from '../v3-overview-card-header/v3-overview-card-header';

// V3 main page's "Analysten" card — restyles V2's AnalystsOverview (ratings/price-target
// gauge, market_analyst_sentiment). Sits between News and Scores in V3Overview. AI-589.
// Desktop content and data derivation are shared with V3AnalystsAnalysis — see CLAUDE.md.
@Component({
  selector: 'app-v3-analysts-overview',
  imports: [V3AnalystsDesktopContent, V3AnalystsStatsRow, V3OverviewCardHeader],
  standalone: true,
  templateUrl: './v3-analysts-overview.html',
  styleUrl: './v3-analysts-overview.scss',
})
export class V3AnalystsOverview {
  private readonly ctx = v3ComponentContext();

  // input(), not @Input() — plain @Input() properties aren't tracked as computed()
  // dependencies, so `potential` would only ever pick up whichever price existed the first
  // time it happened to run, never a later live-price update (the "only updates on hot
  // reload" bug).
  readonly currentPrice = input<number | null>(null);
  @Output() navigate = new EventEmitter<void>();

  readonly isZeroDesktop = this.ctx.layout.isZeroDesktop;
  readonly isFinnetDesktop = this.ctx.layout.isFinnetDesktop;
  readonly isFinnet = this.ctx.layout.isFinnet;
  readonly isDesktop = this.ctx.layout.isDesktop;

  private readonly data = analystsAnalysisData(this.currentPrice, this.isFinnet);

  // Same analysis V2's AnalystsOverview reads — both show the same "last updated" timestamp.
  readonly updatedAt = updatedAtSignal(this.data.analystsAnalysis);

  // Hidden entirely when the analysis is missing/cache_miss/has_data:false — in step with
  // Core.isTabVisible, like every other overview card (AI-640).
  readonly visible = computed(() => !!this.data.analystsAnalysis());

  readonly hasContent = this.data.hasContent;
  readonly rating = this.data.rating;
  readonly isPositive = this.data.isPositive;
  readonly buyCount = this.data.buyCount;
  readonly holdCount = this.data.holdCount;
  readonly sellCount = this.data.sellCount;
  readonly gaugeValue = this.data.gaugeValue;
  readonly sellGaugeValue = this.data.sellGaugeValue;
  readonly potential = this.data.potential;

  // No tabs here — theses follow the consensus (same isPositive as the header), not a tab.
  readonly theses = this.data.consensusTheses;

  readonly expandedIndex = signal<number | null>(null);

  toggleThesis(index: number) {
    this.expandedIndex.update((current) => (current === index ? null : index));
  }

  onNavigate() {
    this.navigate.emit();
  }
}
